
import { Vue, Options, prop } from "vue-class-component";
import { getToken } from "@/app/infrastructures/misc/Cookies";
import parsingLinkGcpToAws from "@/app/infrastructures/misc/common-library/ParsingLinkGcpToAws";
import needsAuth from "@/app/infrastructures/misc/common-library/NeedsAuth";

class Props {
  src = prop<string>({ required: true });
  alt = prop<string>({ default: "image" });
}

@Options({
  name: "AuthorizedImage",
  inheritAttrs: false,
  watch: {
    src: "onSrcChanged"
  }
})
export default class AuthorizedImage extends Vue.with(Props) {
  resolvedSrc!: string | undefined;
  private controller: AbortController | null = null;
  private objectUrl: string | null = null;
  // 1x1 transparent PNG as placeholder so <img> always has a src
  private readonly placeholder: string =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVQIW2P8z/CfBwAFgwJ/lb1tJwAAAABJRU5ErkJggg==";

  data() {
    return {
      resolvedSrc: undefined as string | undefined
    };
  }

  get normalizedSrc(): string | undefined {
    const url = this.src;
    if (!url) return undefined;
    // Allow blob: and data: as-is
    if (url.startsWith("blob:") || url.startsWith("data:")) return url;
    // If starts with //, prepend https:
    if (url.startsWith("//")) return `https:${url}`;
    // If missing protocol but looks like a domain, prepend https://
    if (!/^https?:\/\//i.test(url)) return `https://${url}`;
    return url;
  }

  get displaySrc(): string | undefined {
    const url = this.normalizedSrc;
    if (!url) return this.placeholder;
    // For public URLs, show immediately without waiting for mounted hook
    if (!needsAuth(url)) return url;
    // For protected URLs, show placeholder until resolvedSrc is ready
    return this.resolvedSrc || this.placeholder;
  }

  mounted() {
    this.resolve(this.normalizedSrc);
  }

  beforeUnmount() {
    this.abort();
    this.revoke();
  }

  onSrcChanged(val: string) {
    // Recompute on change
    const next = this.normalizedSrc;
    this.resolve(next);
  }

  private revoke() {
    if (this.objectUrl) {
      try {
        URL.revokeObjectURL(this.objectUrl);
      } catch (e) {
        /* noop */
      }
      this.objectUrl = null;
    }
  }

  private abort() {
    if (this.controller) {
      try {
        this.controller.abort();
      } catch (e) {
        /* noop */
      }
      this.controller = null;
    }
  }

  private async resolve(url?: string) {
    this.abort();
    this.revoke();
    if (!url) {
      this.resolvedSrc = undefined;
      return;
    }
    if (!needsAuth(url)) {
      this.resolvedSrc = url;
      return;
    }

    try {
      const parsed = parsingLinkGcpToAws(url);
      this.controller = new AbortController();
      const headers = new Headers();
      headers.append("authorization", `Bearer ${getToken()}`);
      const res = await fetch(parsed, {
        headers,
        signal: this.controller.signal
      });
      if (!res.ok) {
        // fallback to direct src if auth fetch fails
        this.resolvedSrc = url;
        return;
      }
      const blob = await res.blob();
      this.objectUrl = URL.createObjectURL(blob);
      this.resolvedSrc = this.objectUrl;
    } catch (e) {
      this.resolvedSrc = url;
    }
  }
}
